import React from 'react';
import {CallInfoCtx, CallStatus} from 'components/dataProviders/callInfo';
import {Slide, Paper, Divider} from '@material-ui/core';
import {RaymanUser, Button} from 'components/core';
import {makeStyles} from '@material-ui/core/styles';
import {CallEnd, SystemUpdateAlt, Phone, Videocam, AspectRatio} from '@material-ui/icons';
import clsx from 'clsx';
import AppendElements from 'components/core/AppendChildren';
import VideoCallPanel from './VideoCallPanel';

const style = makeStyles((theme) => ({
    banner: {
      padding: '24px 32px 16px',
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.text.secondary,
      width: 500,
      height: 112,
      position: 'absolute',
      bottom: 32,
      left: "calc(50% - 250px)",
      zIndex: 1000,
      display: 'flex',
      flexDirection: "row",
      '& .MuiTypography-root':
      {
          color: theme.palette.background.paper
      },
      '&.withCam':
      {
        width: 650,
        left: "calc(50% - 325px)",
      }
    },
    remoteVideo:
    {
        height: "calc(100% - 110px)",
        position: "relative",
        '& .remoteVideo, video':
        {
            objectFit: 'contain',
            width: '100%',
            height: '100%',
        }
    },
    localVideoFull:
    {
        width: 300,
        height: 200,
        position: "absolute",
        bottom: 30,
        right: 30,
        '& video':
        {
            objectFit: 'contain',
            width: '80%',
            height: '80%',
            // boxShadow: 'rgba(0, 0, 0, 0.75) 0px 0px 15px 0px',
            filter: 'drop-shadow(0px 0px 15px rgba(0,0,0,.75))'
        }
    },
    localVideoPopup:
    {
        width: 150,
        height: 150,
        '& video':
        {
            objectFit: 'contain',
            width: '80%',
            height: '80%',
            // boxShadow: 'rgba(0, 0, 0, 0.75) 0px 0px 15px 0px',
            filter: 'drop-shadow(0px 0px 15px rgba(0,0,0,.75))'
        }
    },
    expanded: {
        padding: '24px 32px 16px',
        color: theme.palette.background.paper,
        backgroundColor: theme.palette.text.secondary,
        width: 'calc(100vw - 518px)',
        height: '100vh',
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 2000,
        borderRadius: 0,
        '& .MuiTypography-root':
        {
            color: theme.palette.background.paper
        }
    },

    minimized: {
        padding: '24px 32px 16px',
        color: theme.palette.background.paper,
        backgroundColor: theme.palette.text.secondary,
        width: '235px',
        height: '337px',
        position: 'absolute',
        left: 16,
        bottom: 16,
        zIndex: 2000,
        '& .MuiTypography-root':
        {
            color: theme.palette.background.paper,
            textAlign: 'center'
        }
    },

    minimizedFooter: {
        display: 'flex',
        justifyContent: 'center'
    },

    callHeader:
    {
        padding: '24px 32px 16px',
        display: 'flex',
        flexDirection: "row",
        height: 90
    },
    bannerButton:
    {
        height: 56,
        width: 56
    },
    button:
    {
        width: 40,
        height: 40,
        color: theme.palette.background.paper,
        margin: 4
    },
    buttonRed:
    {
        backgroundColor: theme.palette.error.main,
    },
    buttonBlack:
    {
        backgroundColor: theme.palette.text.secondary,
        border: `solid 2px ${theme.palette.background.paper}`
    },
    buttonContainer:
    {
        display: 'flex',
        flexDirection: 'row'
    },
    user:
    {
        flexGrow: 1
    }
  }), {name: "VideoCall"});

export default function VideocallInterface(props)
{
    const ctx = React.useContext(CallInfoCtx);
    const [label, setLabel] = React.useState("");
    const [minimized, setMinimized] = React.useState(false);
    const [localVideo, setLocalVideo] = React.useState(null);
    const classes = style();

    React.useEffect(() => {
        if(ctx.info.status === CallStatus.Live) setLabel("En llamada");
        else if(ctx.info.status === CallStatus.Incoming) setLabel("Llamada entrante");
        else if(ctx.info.status === CallStatus.Calling) setLabel("Llamando...");
        
        if(ctx.info.videoElements.local !== localVideo) setLocalVideo(ctx.info.videoElements.local);
    }, [ctx, localVideo]);

    const hangButton = React.useCallback(() => {
        if(ctx.info.status === CallStatus.Incoming)
        {
            ctx.funcs.rejectCall();
        }
        else
        {
            ctx.funcs.endCall();
        }
    }, [ctx]);

    const acceptCall = React.useCallback((webcam) => {
        ctx.funcs.acceptCall(webcam);
    }, [ctx]);


    
    return <><Slide direction="up" in={(ctx.info.inCall && !ctx.info.videoElements.remote)} mountOnEnter unmountOnExit>
        <Paper className={clsx(classes.banner, localVideo ? "withCam" : "")} elevation={4}>
            {localVideo ? 
            <div className={classes.localVideoPopup}>
                <AppendElements className="selfCamera" htmlElements={localVideo}></AppendElements>
            </div>
            :
            <></>
            }
            <div className={classes.user}>
                <RaymanUser userID={ctx.info.caller} size={56} label={label}/>
            </div>
            <div className={classes.buttonContainer}>
                {ctx.info.status === CallStatus.Incoming && <Button color="primary" className={clsx(classes.button, classes.bannerButton)} onClick={()=>acceptCall(false)}><Phone /></Button>}
                {ctx.info.status === CallStatus.Incoming && <Button color="primary" className={clsx(classes.button, classes.bannerButton)} onClick={()=>acceptCall(true)}><Videocam /></Button>}
                <Button color="error" className={clsx(classes.button, classes.bannerButton, classes.buttonRed)} onClick={()=>hangButton()}><CallEnd /></Button>
            </div>
        </Paper>
    </Slide>
    {ctx.info.inCall && !minimized && ctx.info.videoElements.remote && ctx.info.status === CallStatus.Live?
    <Paper className={classes.expanded} elevation={0}>
        <div className={classes.callHeader}>
            <div className={classes.user}>
                <RaymanUser userID={ctx.info.caller} />
            </div>
            <div className={classes.buttonContainer}>
                <Button color="grey" className={clsx(classes.button, classes.buttonBlack)} onClick={()=>setMinimized(true)}><SystemUpdateAlt /></Button>
                <Button color="error" className={clsx(classes.button, classes.buttonRed)} onClick={()=>hangButton()}><CallEnd /></Button>
            </div>
        </div>
        <div className={classes.remoteVideo}>
            <VideoCallPanel operationID={ctx.info.operationID} userID={ctx.info.caller}/>
        </div>
        <div className={classes.localVideoFull}>
        {localVideo ? 
            <AppendElements className="selfCamera" htmlElements={localVideo}></AppendElements>
            :
            <></>
            }
        </div>
    </Paper>
    // : null}
    : ctx.info.inCall && minimized && ctx.info.videoElements.remote && ctx.info.status === CallStatus.Live ? 
    // {/* { true ?  */}
    <Paper className={classes.minimized} elevation={2}>
        <div className={classes.minimizedHeader}>
            <RaymanUser userID={ctx.info.caller} nopicture />
        </div>
        <div className={classes.remoteVideo}>
            <VideoCallPanel operationID={ctx.info.operationID} userID={ctx.info.caller}/>
        </div>
        <div className={classes.minimizedFooter}>
                <Button color="grey" className={clsx(classes.button, classes.buttonBlack)} onClick={()=>setMinimized(false)}><AspectRatio /></Button>
                <Button color="error" className={clsx(classes.button, classes.buttonRed)} onClick={()=>hangButton()}><CallEnd /></Button>
        </div>
    </Paper>
    : null } 
    </>
}
